/**
 * SmartImprove Value Benchmarks (June 2026)
 *
 * Simplified replacement for the legacy heavyweight "ROI Models". A single
 * global benchmark set with three sections: Workforce Information, Value
 * Drivers & Benchmarks (4 groups), Realization & Risk.
 *
 * CAPTURE-ONLY admin foundation for the FUTURE recommendation-level ROI
 * engine. Nothing here is wired into the live ROI calculation. There is NO
 * ROI % and NO investment field (investment comes from implementation plans).
 * Styling mirrors the SmartEvaluate ROI admin (RoiPage).
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Save, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { valueBenchmarksApi } from "../../lib/smartimproveAdminApi";

const num = (v) => (v === "" || v === null || v === undefined ? "" : v);

function Section({ title, subtitle, children, testid }) {
  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm mb-5"
      data-testid={testid}
    >
      <div className="px-4 py-3 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
        {subtitle ? (
          <p className="text-[11px] text-[#6B7280] mt-0.5">{subtitle}</p>
        ) : null}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function LabeledNumber({ label, value, onChange, suffix, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11px] text-[#374151] font-medium">{label}</span>
      <div className="flex items-center gap-1 mt-1">
        <Input
          type="number"
          value={num(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || ""}
          className="rounded-sm text-xs"
          data-testid={testid}
        />
        {suffix ? (
          <span className="text-[11px] text-[#6B7280] shrink-0">{suffix}</span>
        ) : null}
      </div>
    </label>
  );
}

function MetricRow({ metric, onPatch }) {
  const blank =
    metric.low === null && metric.expected === null && metric.high === null;
  return (
    <div
      className="grid grid-cols-12 gap-2 items-start py-2.5 border-b border-[#F1F5F9] last:border-0"
      data-testid={`vb-metric-${metric.metric_id}`}
    >
      <div className="col-span-12 md:col-span-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-[#111827]">{metric.name}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <code className="text-[9px] text-[#9CA3AF]">{metric.metric_id}</code>
        </div>
        <div className="text-[10px] font-medium text-[#025979] mt-0.5">
          Unit: {metric.unit}
        </div>
        {metric.needs_validation ? (
          <span
            className="inline-flex items-center gap-1 mt-1 text-[9px] px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]"
            data-testid={`vb-needsval-${metric.metric_id}`}
          >
            <AlertTriangle className="w-2.5 h-2.5" /> Needs validation
          </span>
        ) : null}
        {metric.is_conversion_factor ? (
          <span className="inline-block mt-1 ml-1 text-[9px] px-1.5 py-0.5 rounded-sm bg-[#EDE9FE] text-[#5B21B6] border border-[#DDD6FE]">
            conversion rate (not a benefit %)
          </span>
        ) : null}
        {metric.shared_pool ? (
          <span className="inline-block mt-1 ml-1 text-[9px] px-1.5 py-0.5 rounded-sm bg-[#E0F2FE] text-[#075985] border border-[#BAE6FD]">
            shared {metric.benefit_pool} pool
          </span>
        ) : null}
        <div className="text-[9px] text-[#9CA3AF] mt-1">
          pool: {metric.benefit_pool} · cat: {metric.economic_category}
          {metric.unit && metric.unit !== "%" ? ` · unit: ${metric.unit}` : ""}
          {metric.source_type ? ` · ${metric.source_type.replace(/_/g, " ")}` : ""}
        </div>
      </div>
      <div className="col-span-4 md:col-span-1">
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={num(metric.low)}
            onChange={(e) => onPatch({ low: e.target.value })}
            placeholder="Low"
            className="rounded-sm text-xs"
            data-testid={`vb-low-${metric.metric_id}`}
          />
          <span className="text-[10px] text-[#6B7280] shrink-0">{metric.unit_abbrev || "%"}</span>
        </div>
      </div>
      <div className="col-span-4 md:col-span-1">
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={num(metric.expected)}
            onChange={(e) => onPatch({ expected: e.target.value })}
            placeholder="Exp."
            className="rounded-sm text-xs"
            data-testid={`vb-expected-${metric.metric_id}`}
          />
          <span className="text-[10px] text-[#6B7280] shrink-0">{metric.unit_abbrev || "%"}</span>
        </div>
      </div>
      <div className="col-span-4 md:col-span-1">
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={num(metric.high)}
            onChange={(e) => onPatch({ high: e.target.value })}
            placeholder="High"
            className="rounded-sm text-xs"
            data-testid={`vb-high-${metric.metric_id}`}
          />
          <span className="text-[10px] text-[#6B7280] shrink-0">{metric.unit_abbrev || "%"}</span>
        </div>
      </div>
      <div className="col-span-12 md:col-span-3">
        <Textarea
          rows={2}
          value={metric.basis || ""}
          onChange={(e) => onPatch({ basis: e.target.value })}
          placeholder="Benchmark basis / description"
          className="rounded-sm text-xs"
          data-testid={`vb-basis-${metric.metric_id}`}
        />
      </div>
      <div className="col-span-6 md:col-span-2">
        <Input
          value={metric.source || ""}
          onChange={(e) => onPatch({ source: e.target.value })}
          placeholder="Source"
          className="rounded-sm text-xs"
          data-testid={`vb-source-${metric.metric_id}`}
        />
      </div>
      <div className="col-span-6 md:col-span-1">
        <Input
          value={metric.reference || ""}
          onChange={(e) => onPatch({ reference: e.target.value })}
          placeholder="URL / ref"
          className="rounded-sm text-xs"
          data-testid={`vb-reference-${metric.metric_id}`}
        />
      </div>
    </div>
  );
}

function DriverGroup({ group, onMetricPatch, open, onToggle }) {
  const validated = group.metrics.filter((m) => !m.needs_validation).length;
  return (
    <div
      className="border border-[#E5E7EB] rounded-sm mb-3"
      data-testid={`vb-group-${group.group_id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F8FAFC] hover:bg-[#F1F5F9] transition-colors rounded-t-sm"
        data-testid={`vb-group-toggle-${group.group_id}`}
      >
        <span className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="w-4 h-4 text-[#025979]" />
          ) : (
            <ChevronRight className="w-4 h-4 text-[#6B7280]" />
          )}
          <span className="text-xs font-semibold text-[#111827]">
            {group.group_name}
          </span>
          <span className="text-[10px] text-[#6B7280]">
            {validated}/{group.metrics.length} validated
          </span>
        </span>
      </button>
      {open ? (
        <div className="px-3 pb-2">
          {group.description ? (
            <p className="text-[10.5px] text-[#6B7280] py-2">{group.description}</p>
          ) : null}
          <div className="hidden md:grid grid-cols-12 gap-2 text-[9px] uppercase tracking-wider text-[#9CA3AF] pb-1 border-b border-[#E5E7EB]">
            <div className="col-span-3">Metric</div>
            <div className="col-span-1">Low</div>
            <div className="col-span-1">Expected</div>
            <div className="col-span-1">High</div>
            <div className="col-span-3">Benchmark basis</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-1">Ref</div>
          </div>
          {group.metrics.map((m) => (
            <MetricRow
              key={m.metric_id}
              metric={m}
              onPatch={(patch) => onMetricPatch(group.group_id, m.metric_id, patch)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

const TABS = [
  ["workforce", "Workforce Information"],
  ["drivers", "Value Drivers & Benchmarks"],
  ["applicability", "Process Applicability"],
  ["strategic", "Strategic Project Benchmarks"],
  ["realization", "Realization & Risk"],
];

export default function ValueBenchmarksPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("workforce");
  const [openGroups, setOpenGroups] = useState({});
  const [openStrategic, setOpenStrategic] = useState(() => new Set());
  const [confirmDeleteStrategic, setConfirmDeleteStrategic] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    valueBenchmarksApi
      .get()
      .then((d) => {
        setData(d);
        const og = {};
        (d.value_drivers || []).forEach((g, i) => (og[g.group_id] = i === 0));
        setOpenGroups(og);
      })
      .catch(() => toast.error("Failed to load Value Benchmarks"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchWorkforce = (k, v) =>
    setData((d) => ({ ...d, workforce: { ...d.workforce, [k]: v } }));
  const patchRealization = (k, v) =>
    setData((d) => ({ ...d, realization: { ...d.realization, [k]: v } }));
  const patchMetric = (groupId, metricId, patch) =>
    setData((d) => ({
      ...d,
      value_drivers: d.value_drivers.map((g) =>
        g.group_id !== groupId
          ? g
          : {
              ...g,
              metrics: g.metrics.map((m) =>
                m.metric_id !== metricId ? m : { ...m, ...patch }
              ),
            }
      ),
    }));

  const patchProcess = (processId, patch) =>
    setData((d) => ({
      ...d,
      process_applicability: (d.process_applicability || []).map((p) =>
        p.process_id !== processId ? p : { ...p, ...patch }
      ),
    }));

  const patchStrategic = (idx, patch) =>
    setData((d) => ({
      ...d,
      strategic_project_benchmarks: (d.strategic_project_benchmarks || []).map(
        (p, i) => (i !== idx ? p : { ...p, ...patch })
      ),
    }));

  const toggleStrategic = (idx) =>
    setOpenStrategic((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const addStrategic = () => {
    setData((d) => {
      const list = d.strategic_project_benchmarks || [];
      const newIdx = list.length;
      setOpenStrategic((prev) => new Set(prev).add(newIdx));
      return {
        ...d,
        strategic_project_benchmarks: [
          ...list,
          {
            _isNew: true,
            project_id: "",
            project_name: "",
            description: "",
            economic_pool: "labor",
            baseline_source: "warehouse direct labor baseline",
            value_driver_metric_id: "",
            low_pct: null,
            expected_pct: null,
            high_pct: null,
            source: "",
            basis: "",
            needs_validation: true,
            active: true,
          },
        ],
      };
    });
  };

  const removeStrategic = (idx) => {
    setData((d) => ({
      ...d,
      strategic_project_benchmarks: (d.strategic_project_benchmarks || []).filter(
        (_, i) => i !== idx
      ),
    }));
    setConfirmDeleteStrategic(null);
    setOpenStrategic((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i < idx) next.add(i);
        else if (i > idx) next.add(i - 1);
      });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Coerce blanks to null and mark needs_validation off once a value exists.
      const coerce = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = {
        ...data,
        value_drivers: (data.value_drivers || []).map((g) => ({
          ...g,
          metrics: g.metrics.map((m) => {
            const low = coerce(m.low);
            const expected = coerce(m.expected);
            const high = coerce(m.high);
            const hasAny = [low, expected, high].some((x) => x !== null);
            return { ...m, low, expected, high, needs_validation: !hasAny };
          }),
        })),
        process_applicability: (data.process_applicability || []).map((p) => {
          const low = coerce(p.low_pct);
          const expected = coerce(p.expected_pct);
          const high = coerce(p.high_pct);
          const hasAny = [low, expected, high].some((x) => x !== null);
          return { ...p, low_pct: low, expected_pct: expected, high_pct: high, needs_validation: !hasAny };
        }),
        strategic_project_benchmarks: (data.strategic_project_benchmarks || []).map((p) => {
          const low = coerce(p.low_pct);
          const expected = coerce(p.expected_pct);
          const high = coerce(p.high_pct);
          const hasAny = [low, expected, high].some((x) => x !== null);
          return {
            ...p,
            low_pct: low,
            expected_pct: expected,
            high_pct: high,
            needs_validation: !hasAny,
          };
        }),
      };
      const saved = await valueBenchmarksApi.put(payload);
      setData(saved);
      toast.success("Value Benchmarks saved");
    } catch {
      toast.error("Failed to save Value Benchmarks");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="p-6 text-sm text-[#6B7280]" data-testid="vb-loading">
        Loading Value Benchmarks…
      </div>
    );
  }

  const wf = data.workforce || {};
  const rz = data.realization || {};
  const procs = data.process_applicability || [];
  const strategic = data.strategic_project_benchmarks || [];
  const metricOptions = (data.value_drivers || []).flatMap((g) =>
    (g.metrics || []).map((m) => ({ id: m.metric_id, name: m.name, group: g.group_name }))
  );

  return (
    <div className="space-y-4" data-testid="value-benchmarks-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1
            className="text-xl font-bold text-[#111827]"
            style={{ fontFamily: "Chivo" }}
          >
            SmartImprove Value Benchmarks
          </h1>
          <p className="text-[11px] text-[#6B7280] mt-1 max-w-3xl">
            Global operational-improvement benchmarks that will power future
            recommendation-level ROI. ROI and investment are NOT stored here —
            ROI is derived from client warehouse baselines, these benchmark
            improvement ranges, and finalized implementation plans.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs shrink-0"
          data-testid="vb-save-btn"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {saving ? "Saving…" : "Save Benchmarks"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[#E5E7EB]">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            data-testid={`vb-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-3 py-2 text-xs font-medium rounded-t-sm transition-colors ${
              tab === key
                ? "text-[#025979] border-b-2 border-[#025979] -mb-px"
                : "text-[#6B7280] hover:text-[#111827]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "workforce" ? (
        <Section
          title="Workforce Information"
          subtitle="Reference working-calendar assumptions and the PrimeS4 fallback labor cost. Customer-specific workforce count and labor cost are captured at warehouse level, not here."
          testid="vb-section-workforce"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
            <LabeledNumber
              label="Hours per Week"
              value={wf.hours_per_week}
              onChange={(v) => patchWorkforce("hours_per_week", v)}
              suffix="hrs"
              testid="vb-hours-per-week"
            />
            <LabeledNumber
              label="Days per Week"
              value={wf.days_per_week}
              onChange={(v) => patchWorkforce("days_per_week", v)}
              suffix="days"
              testid="vb-days-per-week"
            />
            <LabeledNumber
              label="Weeks per Year"
              value={wf.weeks_per_year}
              onChange={(v) => patchWorkforce("weeks_per_year", v)}
              suffix="wks"
              testid="vb-weeks-per-year"
            />
          </div>
          <div className="mt-5 pt-4 border-t border-[#F1F5F9] max-w-3xl">
            <LabeledNumber
              label="Default Loaded Labor Cost / Hour (USD) — PrimeS4 benchmark"
              value={wf.default_loaded_labor_cost_per_hour_usd}
              onChange={(v) =>
                patchWorkforce("default_loaded_labor_cost_per_hour_usd", v === "" ? null : Number(v))
              }
              suffix="USD/hr"
              placeholder="Needs validation"
              testid="vb-default-labor-cost"
            />
            <p className="text-[10.5px] text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-sm px-2 py-1.5 mt-2">
              Fallback used ONLY when a warehouse explicitly selects “Use PrimeS4
              benchmark”. Never applied when a warehouse labor cost is simply
              blank. Not wired to ROI yet.
            </p>
          </div>
        </Section>
      ) : null}

      {tab === "drivers" ? (
        <Section
          title="Value Drivers & Benchmarks"
          subtitle="Four value-driver groups. Each metric has a stable ID, an economic category, and a benefit pool (the $ pool it ultimately draws from) so future overlap detection can avoid double counting. Enter Low / Expected / High only where a defensible benchmark basis exists."
          testid="vb-section-drivers"
        >
          {(data.value_drivers || []).map((g) => (
            <DriverGroup
              key={g.group_id}
              group={g}
              open={!!openGroups[g.group_id]}
              onToggle={() =>
                setOpenGroups((o) => ({ ...o, [g.group_id]: !o[g.group_id] }))
              }
              onMetricPatch={patchMetric}
            />
          ))}
        </Section>
      ) : null}

      {tab === "applicability" ? (
        <Section
          title="Process Applicability"
          subtitle="Process applicability estimates the share of warehouse labor associated with or affected by an operational process. Values are PrimeS4 planning benchmarks and should be replaced by customer actuals where available. Applicability % × Improvement % determines the potential affected benefit — applicability itself is NOT a savings rate. Never add applicability % and improvement %."
          testid="vb-section-applicability"
        >
          <div className="hidden md:grid grid-cols-12 gap-2 text-[9px] uppercase tracking-wider text-[#9CA3AF] pb-1 border-b border-[#E5E7EB]">
            <div className="col-span-3">Process</div>
            <div className="col-span-1">Pool</div>
            <div className="col-span-1">Low</div>
            <div className="col-span-1">Expected</div>
            <div className="col-span-1">High</div>
            <div className="col-span-3">Benchmark basis</div>
            <div className="col-span-1">Source</div>
            <div className="col-span-1">Ref</div>
          </div>
          {procs.map((p) => (
            <div
              key={p.process_id}
              className="grid grid-cols-12 gap-2 items-start py-2.5 border-b border-[#F1F5F9] last:border-0"
              data-testid={`vb-process-${p.process_id}`}
            >
              <div className="col-span-12 md:col-span-3">
                <span className="text-xs font-medium text-[#111827]">{p.process_name}</span>
                <div><code className="text-[9px] text-[#9CA3AF]">{p.process_id}</code></div>
                {p.needs_validation ? (
                  <span
                    className="inline-flex items-center gap-1 mt-1 text-[9px] px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]"
                    data-testid={`vb-proc-needsval-${p.process_id}`}
                  >
                    <AlertTriangle className="w-2.5 h-2.5" /> Needs validation
                  </span>
                ) : null}
              </div>
              <div className="col-span-3 md:col-span-1">
                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#E0F2FE] text-[#075985] border border-[#BAE6FD] uppercase">
                  {p.economic_pool}
                </span>
              </div>
              {["low_pct", "expected_pct", "high_pct"].map((f) => (
                <div className="col-span-3 md:col-span-1" key={f}>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={num(p[f])}
                      onChange={(e) => patchProcess(p.process_id, { [f]: e.target.value })}
                      placeholder={f === "low_pct" ? "Low" : f === "expected_pct" ? "Exp." : "High"}
                      className="rounded-sm text-xs"
                      data-testid={`vb-${f}-${p.process_id}`}
                    />
                    <span className="text-[10px] text-[#6B7280] shrink-0">%</span>
                  </div>
                </div>
              ))}
              <div className="col-span-12 md:col-span-3">
                <Textarea
                  rows={2}
                  value={p.basis || ""}
                  onChange={(e) => patchProcess(p.process_id, { basis: e.target.value })}
                  placeholder="Benchmark basis / description"
                  className="rounded-sm text-xs"
                  data-testid={`vb-proc-basis-${p.process_id}`}
                />
              </div>
              <div className="col-span-6 md:col-span-1">
                <Input
                  value={p.source || ""}
                  onChange={(e) => patchProcess(p.process_id, { source: e.target.value })}
                  placeholder="Source"
                  className="rounded-sm text-xs"
                  data-testid={`vb-proc-source-${p.process_id}`}
                />
              </div>
              <div className="col-span-6 md:col-span-1">
                <Input
                  value={p.reference || ""}
                  onChange={(e) => patchProcess(p.process_id, { reference: e.target.value })}
                  placeholder="URL / ref"
                  className="rounded-sm text-xs"
                  data-testid={`vb-proc-reference-${p.process_id}`}
                />
              </div>
            </div>
          ))}
        </Section>
      ) : null}

      {tab === "strategic" ? (
        <Section
          title="Strategic Project Benchmarks"
          subtitle="Top-down benchmarks used to calculate ROI for Strategic Recommendations SEPARATELY from the bottom-up Primary Recommendation model — a strategic project's benefit is NOT the sum of its component benefits. Applicability % × Improvement % logic still applies against the selected baseline. Not yet wired into the live ROI calc."
          testid="vb-section-strategic"
        >
          <div className="flex justify-end mb-3">
            <Button
              onClick={addStrategic}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              data-testid="vb-strategic-add-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Strategic Project
            </Button>
          </div>
          {strategic.length === 0 ? (
            <p className="text-xs text-[#6B7280] py-6 text-center" data-testid="vb-strategic-empty">
              No strategic project benchmarks yet. Click "Add Strategic Project" to create one.
            </p>
          ) : null}
          <div className="space-y-3">
            {strategic.map((p, idx) => {
              const isOpen = openStrategic.has(idx);
              const driverName =
                metricOptions.find((m) => m.id === p.value_driver_metric_id)?.name ||
                (p.value_driver_metric_id ? p.value_driver_metric_id : "—");
              const rng = (v) => (v === null || v === undefined || v === "" ? "—" : `${v}%`);
              const isExisting = !p._isNew;
              return (
                <div
                  key={idx}
                  className="border border-[#E5E7EB] rounded-md bg-white overflow-hidden"
                  data-testid={`vb-strategic-${p.project_id || idx}`}
                >
                  {/* Collapsed summary header (click to expand/collapse) */}
                  <button
                    type="button"
                    onClick={() => toggleStrategic(idx)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#F9FAFB] transition-colors"
                    data-testid={`vb-strategic-toggle-${p.project_id || idx}`}
                  >
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-[#6B7280] shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-[#6B7280] shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#111827] truncate">
                          {p.project_name || "Untitled strategic project"}
                        </span>
                        {p.needs_validation ? (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]"
                            data-testid={`vb-strategic-needsval-${p.project_id || idx}`}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> Needs validation
                          </span>
                        ) : null}
                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded-sm border ${
                            p.active
                              ? "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"
                              : "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]"
                          }`}
                          data-testid={`vb-strategic-statusbadge-${p.project_id || idx}`}
                        >
                          {p.active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] text-[#6B7280]">
                        <span>
                          Pool: <span className="text-[#374151]">{p.economic_pool || "—"}</span>
                        </span>
                        <span className="truncate">
                          Driver: <span className="text-[#374151]">{driverName}</span>
                        </span>
                        <span>
                          Range:{" "}
                          <span className="text-[#374151]">
                            {rng(p.low_pct)} / {rng(p.expected_pct)} / {rng(p.high_pct)}
                          </span>
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Expanded edit form */}
                  {isOpen ? (
                    <div className="border-t border-[#E5E7EB] p-4 space-y-4">
                      {/* Header row: active toggle + system status */}
                      <div className="flex items-center justify-between gap-2">
                        <label className="inline-flex items-center gap-2 text-[11px] text-[#374151] font-medium">
                          <input
                            type="checkbox"
                            checked={!!p.active}
                            onChange={(e) => patchStrategic(idx, { active: e.target.checked })}
                            data-testid={`vb-strategic-active-${p.project_id || idx}`}
                          />
                          Active
                        </label>
                        <span
                          className="text-[10px] text-[#6B7280]"
                          data-testid={`vb-strategic-sysstatus-${p.project_id || idx}`}
                        >
                          System status:{" "}
                          {p.needs_validation ? (
                            <span className="text-[#92400E] font-medium">Needs validation</span>
                          ) : (
                            <span className="text-[#166534] font-medium">Validated</span>
                          )}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                          <span className="text-[11px] text-[#374151] font-medium">Project name</span>
                          <Input
                            value={p.project_name || ""}
                            onChange={(e) => patchStrategic(idx, { project_name: e.target.value })}
                            placeholder="e.g. RF / Mobile Modernization"
                            className="rounded-sm text-xs mt-1"
                            data-testid={`vb-strategic-name-${p.project_id || idx}`}
                          />
                        </label>
                        <label className="block">
                          <span className="text-[11px] text-[#374151] font-medium">
                            Project ID {isExisting ? <span className="text-[#9CA3AF]">(read-only)</span> : null}
                          </span>
                          <Input
                            value={p.project_id || ""}
                            onChange={(e) => patchStrategic(idx, { project_id: e.target.value })}
                            readOnly={isExisting}
                            disabled={isExisting}
                            placeholder="e.g. rf_mobile_modernization"
                            className={`rounded-sm text-xs mt-1 font-mono ${
                              isExisting ? "bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed" : ""
                            }`}
                            data-testid={`vb-strategic-id-${p.project_id || idx}`}
                          />
                        </label>
                      </div>

                      {/* Description full width */}
                      <label className="block">
                        <span className="text-[11px] text-[#374151] font-medium">Description</span>
                        <Textarea
                          rows={3}
                          value={p.description || ""}
                          onChange={(e) => patchStrategic(idx, { description: e.target.value })}
                          placeholder="What this strategic project delivers"
                          className="rounded-sm text-xs mt-1"
                          data-testid={`vb-strategic-desc-${p.project_id || idx}`}
                        />
                      </label>

                      {/* Economic pool + Baseline source on one row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="block">
                          <span className="text-[11px] text-[#374151] font-medium">Economic pool</span>
                          <select
                            value={p.economic_pool || "labor"}
                            onChange={(e) => patchStrategic(idx, { economic_pool: e.target.value })}
                            className="w-full rounded-sm text-xs mt-1 border border-[#E5E7EB] h-9 px-2 bg-white"
                            data-testid={`vb-strategic-pool-${p.project_id || idx}`}
                          >
                            <option value="labor">labor</option>
                            <option value="working_capital">working_capital</option>
                            <option value="quality">quality</option>
                            <option value="volume">volume</option>
                          </select>
                        </label>
                        <label className="block">
                          <span className="text-[11px] text-[#374151] font-medium">Baseline source</span>
                          <Input
                            value={p.baseline_source || ""}
                            onChange={(e) => patchStrategic(idx, { baseline_source: e.target.value })}
                            placeholder="warehouse direct labor baseline"
                            className="rounded-sm text-xs mt-1"
                            data-testid={`vb-strategic-baseline-${p.project_id || idx}`}
                          />
                        </label>
                      </div>

                      {/* Value driver full width */}
                      <label className="block">
                        <span className="text-[11px] text-[#374151] font-medium">Value driver</span>
                        <select
                          value={p.value_driver_metric_id || ""}
                          onChange={(e) => patchStrategic(idx, { value_driver_metric_id: e.target.value })}
                          className="w-full rounded-sm text-xs mt-1 border border-[#E5E7EB] h-9 px-2 bg-white"
                          data-testid={`vb-strategic-driver-${p.project_id || idx}`}
                        >
                          <option value="">— Select value driver —</option>
                          {metricOptions.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name} ({m.id})
                            </option>
                          ))}
                        </select>
                      </label>

                      {/* Benchmark Range group */}
                      <fieldset className="border border-[#E5E7EB] rounded-sm p-3">
                        <legend className="text-[10px] uppercase tracking-wide text-[#6B7280] px-1">
                          Benchmark Range
                        </legend>
                        <div className="grid grid-cols-3 gap-3">
                          {["low_pct", "expected_pct", "high_pct"].map((f) => (
                            <div key={f}>
                              <span className="text-[11px] text-[#374151] font-medium">
                                {f === "low_pct" ? "Low %" : f === "expected_pct" ? "Expected %" : "High %"}
                              </span>
                              <div className="flex items-center gap-1 mt-1">
                                <Input
                                  type="number"
                                  value={num(p[f])}
                                  onChange={(e) => patchStrategic(idx, { [f]: e.target.value })}
                                  placeholder={f === "low_pct" ? "Low" : f === "expected_pct" ? "Exp." : "High"}
                                  className="rounded-sm text-xs"
                                  data-testid={`vb-strategic-${f}-${p.project_id || idx}`}
                                />
                                <span className="text-[10px] text-[#6B7280] shrink-0">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </fieldset>

                      {/* Benchmark Provenance group */}
                      <fieldset className="border border-[#E5E7EB] rounded-sm p-3">
                        <legend className="text-[10px] uppercase tracking-wide text-[#6B7280] px-1">
                          Benchmark Provenance
                        </legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label className="block">
                            <span className="text-[11px] text-[#374151] font-medium">Source</span>
                            <Input
                              value={p.source || ""}
                              onChange={(e) => patchStrategic(idx, { source: e.target.value })}
                              placeholder="e.g. PrimeS4 planning benchmark"
                              className="rounded-sm text-xs mt-1"
                              data-testid={`vb-strategic-source-${p.project_id || idx}`}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] text-[#374151] font-medium">Basis</span>
                            <Input
                              value={p.basis || ""}
                              onChange={(e) => patchStrategic(idx, { basis: e.target.value })}
                              placeholder="Benchmark basis / rationale"
                              className="rounded-sm text-xs mt-1"
                              data-testid={`vb-strategic-basis-${p.project_id || idx}`}
                            />
                          </label>
                        </div>
                      </fieldset>

                      {/* Delete with confirmation */}
                      <div className="flex justify-end pt-1 border-t border-[#F3F4F6]">
                        {confirmDeleteStrategic === idx ? (
                          <div className="flex items-center gap-2" data-testid={`vb-strategic-confirm-${p.project_id || idx}`}>
                            <span className="text-[11px] text-[#B91C1C]">Delete this project?</span>
                            <Button
                              variant="ghost"
                              onClick={() => setConfirmDeleteStrategic(null)}
                              className="h-7 px-2 text-[11px] text-[#374151] hover:bg-[#F3F4F6] rounded-sm"
                              data-testid={`vb-strategic-cancel-delete-${p.project_id || idx}`}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => removeStrategic(idx)}
                              className="h-7 px-3 text-[11px] bg-[#B91C1C] hover:bg-[#991B1B] text-white rounded-sm"
                              data-testid={`vb-strategic-confirm-delete-${p.project_id || idx}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            onClick={() => setConfirmDeleteStrategic(idx)}
                            className="h-7 px-2 text-[11px] text-[#B91C1C] hover:bg-[#FEE2E2] rounded-sm"
                            data-testid={`vb-strategic-remove-${p.project_id || idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete project
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Section>
      ) : null}

      {tab === "realization" ? (
        <Section
          title="Realization & Risk"
          subtitle="Global SmartImprove defaults. These will later be override-able at business-case level. Not connected to the live ROI engine in this task."
          testid="vb-section-realization"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            <LabeledNumber
              label="Year 1 Realization %"
              value={rz.year1_realization_pct}
              onChange={(v) => patchRealization("year1_realization_pct", v)}
              suffix="%"
              testid="vb-year1-realization"
            />
            <LabeledNumber
              label="Confidence Multiplier %"
              value={rz.confidence_multiplier_pct}
              onChange={(v) => patchRealization("confidence_multiplier_pct", v)}
              suffix="%"
              testid="vb-confidence-multiplier"
            />
            <LabeledNumber
              label="Conservative Scenario %"
              value={rz.conservative_scenario_pct}
              onChange={(v) => patchRealization("conservative_scenario_pct", v)}
              suffix="%"
              testid="vb-conservative-scenario"
            />
            <LabeledNumber
              label="Expected Scenario %"
              value={rz.expected_scenario_pct}
              onChange={(v) => patchRealization("expected_scenario_pct", v)}
              suffix="%"
              testid="vb-expected-scenario"
            />
            <LabeledNumber
              label="Optimistic Scenario %"
              value={rz.optimistic_scenario_pct}
              onChange={(v) => patchRealization("optimistic_scenario_pct", v)}
              suffix="%"
              testid="vb-optimistic-scenario"
            />
          </div>
          <div className="mt-4 space-y-1.5 max-w-3xl">
            <p className="text-[10.5px] text-[#6B7280]">
              <span className="font-semibold text-[#374151]">Year 1 Realization:</span>{" "}
              {rz.year1_realization_help}
            </p>
            <p className="text-[10.5px] text-[#6B7280]">
              <span className="font-semibold text-[#374151]">Confidence Multiplier:</span>{" "}
              {rz.confidence_multiplier_help}
            </p>
          </div>
        </Section>
      ) : null}
    </div>
  );
}
